import React, { useEffect } from "react";
import { useDebounceValue, useIntersectionObserver } from "usehooks-ts";

import { faSort } from "@fortawesome/pro-duotone-svg-icons";

import { faSearch } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, InputText, Text } from "@performyard/py-components";

import LoadingRespondentListEntry from "./LoadingRespondentListEntry";
import RespondentListEntry from "./RespondentListEntry";

import { useGetSurveyParticipantsInfinite } from "../../../../../../../../../api/hooks/defs/surveys";
import useSearchQueryParams from "../../../../../../../../../hooks/useSearchQueryParams";

import { PARTICIPANT_PAGE_SIZE, searchParamsSchema } from "../utils";

type RespondentsListProps = {
	surveyId: string;
};

const LOADING_SHIMMERS_COUNT = 10;

const RespondentsList = ({ surveyId }: RespondentsListProps) => {
	const { queryParams: searchParams = { search: "", sort: "asc" }, handleQueryChange: setSearchParams } =
		useSearchQueryParams({
			schema: searchParamsSchema,
		});
	const [debouncedSearchParams] = useDebounceValue(searchParams?.search ?? "", 500);

	const {
		data,
		isLoading: isLoadingParticipants,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	} = useGetSurveyParticipantsInfinite(surveyId, {
		search: debouncedSearchParams,
		sort: searchParams?.sort ?? "asc",
		limit: PARTICIPANT_PAGE_SIZE,
	});

	const { isIntersecting, ref: sentinelRef } = useIntersectionObserver({
		threshold: 0,
	});

	useEffect(() => {
		if (isIntersecting && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

	const participants = data?.pages.flatMap((page) => page.participants) ?? [];

	return (
		<section
			id="respondents-list"
			className="flex h-full w-full flex-col overflow-hidden"
			aria-label="Survey responses panel"
		>
			<div className="border-b border-solid border-neutral-2 p-4">
				<div className="flex flex-shrink-0 flex-col gap-2">
					<button
						type="button"
						aria-label={`Sort responses ${searchParams?.sort === "asc" ? "descending" : "ascending"}`}
						data-sort={searchParams?.sort ?? "asc"}
						className="group/sort m-0 inline-flex w-fit cursor-pointer items-center gap-1 border-none bg-transparent p-0"
						onClick={() =>
							setSearchParams({ ...searchParams, sort: searchParams?.sort === "asc" ? "desc" : "asc" })
						}
					>
						<Text size="paragraph-3" weight="semi-bold">
							Responses
						</Text>
						<div className="flex items-center justify-center transition-transform group-data-[sort='asc']/sort:rotate-180">
							<Icon icon={faSort} size={16} color="neutral-5" />
						</div>
					</button>
					<InputText
						leftIcon={faSearch}
						placeholder="Search..."
						aria-label="Search respondents"
						value={searchParams?.search ?? ""}
						onChange={(value) => setSearchParams({ ...searchParams, search: value })}
					/>
				</div>
			</div>
			<ul role="listbox" aria-label="Responses list" className="min-h-0 flex-1 overflow-y-auto">
				{isLoadingParticipants ? (
					Array.from({ length: LOADING_SHIMMERS_COUNT }).map((_, index) => (
						<LoadingRespondentListEntry key={index} />
					))
				) : participants.length === 0 ? (
					<li className="p-4 text-center">
						<Text size="paragraph-3" color="neutral-5">
							No responses found
						</Text>
					</li>
				) : (
					<>
						{participants.map((participant) => (
							<RespondentListEntry key={participant._id} participant={participant} />
						))}
						<li ref={sentinelRef} className="p-4 text-center">
							{isFetchingNextPage ? (
								Array.from({ length: LOADING_SHIMMERS_COUNT }).map((_, index) => (
									<LoadingRespondentListEntry key={`loading-${index}`} />
								))
							) : !hasNextPage ? (
								<Text size="paragraph-3" color="neutral-5">
									All responses loaded
								</Text>
							) : null}
						</li>
					</>
				)}
			</ul>
		</section>
	);
};

export default RespondentsList;
